import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, fromJS } from 'immutable'
import { connect as connectRedux, useDispatch, useSelector } from 'react-redux'
import { compose } from 'recompose'
import _debounce from 'lodash/debounce'
import _isEmpty from 'lodash/isEmpty'
import _pick from 'lodash/pick'
import {
  CONTENT_ROOTS,
  CONTENT_STORE_KEY,
  createContentListViewOptions,
  locationToOptions,
} from 'services/content'
import { CONTENT_TYPE_VIDEO } from 'services/content/content-types'
import { getViewError, findView, getViewProcessing, getViewRecordsAsList } from 'services/data'
import { renderModal as renderModalAction } from 'services/modal/redux/actions'
import connectHistory from 'gui/components/router/connect'
import { createLocationHref } from 'services/location'
import { Container, Grid, MenuItem, Box, Chip } from '@mui/material'
import MuiForm from 'gui/components/common/MuiForm'
import MuiSelectWrapper from 'gui/components/common/MuiSelect'
import MuiCheckbox from 'gui/components/common/MuiCheckbox'
import MuiButton from 'gui/components/common/MuiButton'
import MuiTextFieldWrapper from 'gui/components/common/MuiTextField'
import Loader from 'gui/components/common/Loader'
import Link from 'gui/components/link'
import ContentTypeSelect from 'gui/components/content-type-select'
import LanguageSelect from 'gui/components/language-select'
import ContentSortType from 'gui/components/content-sort-type'
import Pagination from 'gui/components/pagination'
import { formatInTimeZone } from 'date-fns-tz'
import './index.scss'
import { CONFIRM_MODAL } from 'services/modal'
import { getAuthPermissions, PERM_DELETE_CONTENT, PERM_UPDATE_CONTENT } from 'services/auth'
import { selectAuth } from 'services/auth/redux/selectors'
import { deleteContentBulk as deleteContentBulkAction } from 'services/content/redux/actions'
import { getLanguageChipClass } from 'services/languages'

const DEFAULT_LIMIT = 50
const DEFAULT_OFFSET = 0

function ContentListLoader () {
  return (
    <div className="loader-container">
      <Loader />
      <div className="loader__update-mask" />
    </div>
  )
}

/**
 * Handle the page change event
 * @param {Number} page The page
 * @param {Object} props The props
 * @param {History} props.history The history singleton
 */
function onChangePage (page, props) {
  const { data, location, storeKey, history } = props
  const view =
    findView({
      data,
      storeKey,
      options: createContentListViewOptions({ location }),
    }) || Map()
  const offset = view.getIn(['options', 'limit'], DEFAULT_LIMIT) * (page - 1)
  history.push(createLocationHref(location.setIn(['data', 'query', 'offset'], offset)))
}

function setFilterOptionValue (options = {}) {
  const { value, history, name, location, sortDirection } = options

  let href
  let newLocation = location.setIn(['data', 'query', 'offset'], 0)
  if (sortDirection) {
    newLocation = location.setIn(['data', 'query', 'sortDirection'], sortDirection)
  }
  if (value === '') {
    href = createLocationHref(newLocation.deleteIn(['data', 'query', name]))
  } else {
    href = createLocationHref(newLocation.setIn(['data', 'query', name], value))
  }
  history.replace(href)
}

const setFilterOptionValueDebounced = _debounce(setFilterOptionValue, 700)

function onChangeFilterOption (e, props) {
  const { location, history, sortDirection } = props
  const { value, name } = e.target
  setFilterOptionValueDebounced({
    value,
    history,
    name,
    location,
    sortDirection,
  })
}

const getFilterTypeColumn = (row) => {
  const contentType = row.get('contentType')

  if (contentType !== 'tag') {
    return ''
  }

  const bundle = row.get('bundle', '')
  const filterType = row.get('filterType', '')

  return bundle === 'filter_options' || _isEmpty(bundle) ? filterType : bundle
}

function ContentListBody (props) {
  const { data, location, storeKey } = props

  const [selectedRecords, setSelectedRecords] = useState(Map())

  const options = createContentListViewOptions({ location })
  const viewInstance = { data, storeKey, options }
  const records = getViewRecordsAsList(viewInstance)
  const view = findView(viewInstance)
  const offset = options.get('offset', DEFAULT_OFFSET)
  const limit = options.get('limit', DEFAULT_LIMIT)
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

  const auth = useSelector(selectAuth)

  const authPermissions = getAuthPermissions(auth)
  const isViewOnlyMode = !authPermissions.includes(PERM_UPDATE_CONTENT)
  const canDelete = authPermissions.includes(PERM_DELETE_CONTENT)

  const dispatch = useDispatch()

  if (records.isEmpty()) {
    return (
      <div className="content-list-table__empty">
        <div style={{ fontSize: '48px', marginBottom: '16px', color: '#ccc' }}>📄</div>
        <div>No content found matching your criteria.</div>
        <div style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
          Try adjusting your filters or search terms.
        </div>
      </div>
    )
  }

  const checkAll = ({ target: { checked } }) => {
    if (checked) {
      let newSelectedRecords = Map()
      records.forEach((record) => {
        const id = record.get('id')
        newSelectedRecords = newSelectedRecords.set(id, record)
      })
      setSelectedRecords(Map(fromJS(newSelectedRecords)))
    } else {
      setSelectedRecords((prevValue) => prevValue.clear())
    }
  }

  const checkRecord = (id, item) => {
    if (selectedRecords.has(id)) {
      setSelectedRecords(selectedRecords.delete(id))
    } else {
      setSelectedRecords(selectedRecords.set(id, item))
    }
  }

  const handleDelete = () => {
    const [...contentIds] = selectedRecords.keys()

    dispatch(
      deleteContentBulkAction({
        storeKey,
        contentIds,
      }),
    )
  }

  const openConfirmModal = () => {
    dispatch(
      renderModalAction(CONFIRM_MODAL, {
        innerClass: 'modal__confirm',
        backgroundDismiss: true,
        onConfirm: handleDelete,
        header: 'Delete Content',
        confirmText: `Are you sure you want to delete ${selectedRecords.size} selected content item${selectedRecords.size > 1 ? 's' : ''}?`,
      }),
    )
  }

  return (
    <React.Fragment>
      <div className="content-list-table">
        <Container maxWidth={false}>
          <Grid container spacing={2} style={{ padding: '24px 0 0 0' }}>
            <Grid item xs={1}>
              <strong>Content Id</strong>
            </Grid>
            <Grid item xs={1}>
              <strong>Content Type</strong>
            </Grid>
            <Grid item xs={3}>
              <strong>Title</strong>
            </Grid>
            <Grid item xs={1}>
              <strong>Filter type</strong>
            </Grid>
            <Grid item xs={1}>
              <strong>Language</strong>
            </Grid>
            <Grid item xs={1}>
              <strong>Created Date</strong>
            </Grid>
            <Grid item xs={1}>
              <strong>Published</strong>
            </Grid>
            <Grid item xs={2}>
              <strong>Published At Date</strong>
            </Grid>
            <Grid item xs={1} className="checkbox-column">
              <MuiCheckbox
                onChange={checkAll}
                disabled={records.isEmpty() || isViewOnlyMode || !canDelete}
              />
            </Grid>
          </Grid>
          {records.map((item, i) => (
            <div className="content-list-table__list" key={`content-list-row-${i}`}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={1}>
                  {item.get('contentId')}
                </Grid>
                <Grid item xs={1}>
                  <span
                    style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                    }}
                  >
                    {item.get('contentType')}
                  </span>
                </Grid>
                <Grid item xs={3}>
                  <Link
                    href={
                      item.get('contentType') === CONTENT_TYPE_VIDEO
                        ? `${CONTENT_ROOTS.V2}/${item.get('id')}`
                        : `/content/${item.get('id')}`
                    }
                  >
                    {item.get('title')}
                  </Link>
                </Grid>
                <Grid item xs={1}>
                  {getFilterTypeColumn(item)}
                </Grid>
                <Grid item xs={1}>
                  <Chip
                    label={item.get('language')}
                    size="small"
                    className={`language-chip ${getLanguageChipClass(item.get('language'))}`}
                  />
                </Grid>
                <Grid item xs={1}>
                  {formatInTimeZone(item.get('createdAt'), timeZone, 'MM-dd-yyyy')}
                </Grid>
                <Grid item xs={1}>
                  <span
                    style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      backgroundColor: item.get('published') ? '#e8f5e8' : '#ffebee',
                      color: item.get('published') ? '#2e7d32' : '#c62828',
                    }}
                  >
                    {item.get('published') ? 'Yes' : 'No'}
                  </span>
                </Grid>
                <Grid item xs={2}>
                  {formatInTimeZone(item.get('publishedAtDate'), timeZone, 'MM-dd-yyyy')}
                </Grid>
                <Grid item xs={1} className="checkbox-column">
                  <MuiCheckbox
                    onChange={() => checkRecord(item.get('id'), item)}
                    checked={selectedRecords.has(item.get('id'))}
                    disabled={isViewOnlyMode || !canDelete}
                  />
                </Grid>
              </Grid>
            </div>
          ))}
        </Container>
      </div>

      {/* Delete Button - Hover when items are selected */}
      <Box className={`content-list__delete-button ${!selectedRecords.isEmpty() ? 'visible' : ''}`}>
        <Box className="delete-button-content">
          <span className="selected-count">
            {selectedRecords.size} item{selectedRecords.size > 1 ? 's' : ''} selected
          </span>
          <MuiButton
            variant="contained"
            color="error"
            onClick={openConfirmModal}
            disabled={selectedRecords.isEmpty()}
          >
            Delete
          </MuiButton>
        </Box>
      </Box>

      <Pagination
        onChangePage={(page) => onChangePage(page, props)}
        page={offset / limit + 1}
        itemsPerPage={limit}
        totalItemCount={view.get('total')}
      />
    </React.Fragment>
  )
}

/**
 * Component to create a content list component
 * @param {Object} props The props
 * @param {Map} props.data The data state
 * @param {History} props.history The history singleton
 * @param {Map} props.location The location state
 * @param {String} props.storeKey The store key
 */
function ContentList (props) {
  const { location, data, storeKey } = props
  const options = createContentListViewOptions({ location })
  const processing = getViewProcessing({ data, storeKey, options })
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

  useEffect(() => {
    const error = getViewError({
      data,
      storeKey: CONTENT_STORE_KEY,
      options: locationToOptions(location),
    })
    if (error) {
      alert(error)
    }
  }, [data])

  return (
    <Container className="content-list-data-table content-list-data-table--list" maxWidth={false}>
      <MuiForm className="content-list-filters mui--clearfix">
        <h3 className="content-list-filters__title">Filter Content</h3>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextFieldWrapper
                  className="content-list-filters__input"
                  label="Title"
                  name="title"
                  type="text"
                  defaultValue={options.get('title')}
                  onChange={(e) => onChangeFilterOption(e, props)}
                  placeholder="Search by title..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextFieldWrapper
                  className="content-list-filters__input"
                  label="Content ID"
                  name="contentId"
                  type="number"
                  defaultValue={options.get('contentId')}
                  onChange={(e) => onChangeFilterOption(e, props)}
                  placeholder="Enter content ID..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextFieldWrapper
                  className="content-list-filters__input"
                  label="Media ID"
                  name="mediaId"
                  type="number"
                  defaultValue={options.get('mediaId')}
                  onChange={(e) => onChangeFilterOption(e, props)}
                  placeholder="Enter media ID..."
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ContentTypeSelect
                  label="Content type"
                  value={options.get('contentType')}
                  name="contentType"
                  onChange={(e) => onChangeFilterOption(e, props)}
                  className="content-list-filters__input"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <LanguageSelect
                  label="Language"
                  value={options.get('language')}
                  name="language"
                  onChange={(e) => onChangeFilterOption(e, props)}
                  className="content-list-filters__input"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiSelectWrapper
                  label="Published"
                  name="published"
                  value={options.get('published') || ''}
                  onChange={(e) => onChangeFilterOption(e, props)}
                  className="content-list-filters__input"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </MuiSelectWrapper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <ContentSortType
                  label="Sort By"
                  name="sort"
                  defaultValue={options.get('sort')}
                  onChange={(e) => onChangeFilterOption(e, { ...props, sortDirection: 'desc' })}
                  className="content-list-filters__input"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <MuiTextFieldWrapper
                  className="content-list-filters__input"
                  label="Published At Date"
                  name="publishedAtDate"
                  type="date"
                  value={
                    options.get('publishedAtDate')
                      ? formatInTimeZone(options.get('publishedAtDate'), timeZone, 'yyyy-MM-dd')
                      : ''
                  }
                  onChange={(e) => {
                    const {
                      target: { name, value },
                    } = e
                    onChangeFilterOption(
                      { target: { name, value: formatInTimeZone(value, 'UTC', 'yyyy-MM-dd') } },
                      { ...props, sortDirection: 'asc' },
                    )
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MuiForm>
      {processing && <ContentListLoader />}
      {!processing && <ContentListBody {...props} />}
    </Container>
  )
}

ContentList.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  history: PropTypes.object.isRequired,
  location: ImmutablePropTypes.map.isRequired,
  storeKey: PropTypes.string.isRequired,
}

ContentList.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
}

export default compose(
  connectRedux((state) => _pick(state, ['data', 'location'])),
  connectHistory(),
)(ContentList)
